import { Button } from '@evertrue/et-components';
import { getMomentObjectInTimezone, getDateString } from 'base/new-utils';
import { getUserTaskCountsForToday, getTaskCountForToday } from 'apps/home/utils/api-requests/home-page';
import { useEffect, useState } from 'react';
import AddTaskButton from 'apps/tasks/components/create/add-task-button';
import EverTrue from 'app';
import HomepageCard from '../homepage-card';
import JourneyTaskCount from './journey-task-count';
import OtherTaskCount from './other-task-count';
import PropTypes from 'prop-types';
import TripsAndMeetings from './trips-meetings';
import { useFeatureFlag } from 'entities/helpers/use-feature-flag';

const ACTION_TYPE_FIELDS = {
  CALL: 'Call',
  EMAIL: 'Email',
  LINKEDIN: 'LinkedIn',
  RESEARCH: 'Research',
  PRINT_MAIL: 'Print Mail',
  TEXT: 'Text',
  THANK_VIEW: 'ThankView',
  OTHER: 'Other',
};

const TodayContent = ({ user = {} }) => {
  const [counts, setCounts] = useState([{}, {}]);
  const [loading, setLoading] = useState(false);
  const { flagEnabled: signalEnabled } = useFeatureFlag('signal');

  useEffect(() => {
    const getAndSetJourneyCounts = async () => {
      try {
        setLoading(true);
        const fetchedCounts = await getTaskCountForToday();

        if (signalEnabled && user.id) {
          // When signal is on, bypass the legacy API's opaque org-wide bucketing and
          // compute counts directly from the user's own tasks. This correctly splits
          // cadence 1.0 (journey_task_id), cadence 2.0 (task_source='cadence_v2'),
          // and non-cadence tasks — matching what the tasks page shows.
          const userCounts = await getUserTaskCountsForToday(user.id);
          setCounts(userCounts || fetchedCounts);
        } else {
          setCounts(fetchedCounts);
        }
      } catch (e) {
        console.error('Error fetching today data', e);
      } finally {
        setLoading(false);
      }
    };

    getAndSetJourneyCounts();
  }, [user.id, signalEnabled]);

  const addTaskToCount = (task = {}) => {
    if (!task || !(typeof task === 'object')) return;
    // Only optimistically increment for tasks due today or overdue — matches getUserTaskCountsForToday filter
    const today = getDateString();
    if (!task.current_due_date || task.current_due_date > today) return;

    const { action_type = '' } = task;
    const normalized = action_type.toUpperCase();
    const mapped = normalized in ACTION_TYPE_FIELDS ? normalized : 'OTHER';
    const taskCountCopy = [...counts];
    const [other, journey] = taskCountCopy;
    // When signal flag is on, cadence_v2 tasks (with cadence_id) belong in cadence steps
    const isCadenceStep = signalEnabled ? task?.journey_task_id || task?.cadence_id : task?.journey_task_id;
    if (isCadenceStep) {
      journey[mapped] = (journey[mapped] || 0) + 1;
      journey['TOTAL'] = (journey['TOTAL'] || 0) + 1;
    } else {
      other[mapped] = (other[mapped] || 0) + 1;
      other['TOTAL'] = (other['TOTAL'] || 0) + 1;
    }

    setCounts(taskCountCopy);
  };

  const optionalStateManagementFunction = (type, args = {}) => {
    switch (type) {
      case 'addTask':
        addTaskToCount(args.task);
        break;
      default:
        break;
    }
  };

  return (
    <HomepageCard
      leftHeading={'Today'}
      rightHeading={getMomentObjectInTimezone().format('dddd, MMMM DD, YYYY')}
      body={
        <>
          <OtherTaskCount todayTaskCount={counts[0]} loadingTasksTodayCount={loading} />
          <div className="homepage-today-other-tasks">
            <div className="homepage-today-other-tasks--view">
              <Button
                onClick={() => {
                  EverTrue.track.set('home_screen', { type: 'view_task' });
                  EverTrue.UrlManager.navigate('/tasks', true, false);
                }}
              >
                View Tasks
              </Button>
            </div>
            <div className="homepage-today-other-tasks--add" role="button" tabIndex={0} aria-label="Add task">
              <AddTaskButton
                buttonType={'secondary'}
                optionalStateManagementFunction={optionalStateManagementFunction}
              />
            </div>
          </div>
          <JourneyTaskCount todayJourneyCount={counts[1]} loadingJourneysTodayCount={loading} />
          <div className="homepage-today-cadence-steps--view">
            <Button
              onClick={() => {
                EverTrue.track.set('home_screen', { type: 'cadences' });
                EverTrue.UrlManager.navigate('/cadences', true, false);
              }}
            >
              View Cadences
            </Button>
          </div>
          <hr></hr>
          <TripsAndMeetings />
        </>
      }
    />
  );
};
TodayContent.propTypes = {
  user: PropTypes.object,
};

export default TodayContent;
